import _ from 'lodash'
import { useContext, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Grid } from '@mui/material'

import JoinNetworkRequirementDialog from '@/components/ui/dialogs/JoinNetworkRequirementDialog'
import NetworkCard from '@/components/ui/cards/NetworkCard'

import { useGetNetworksQuery, useSendNetworkJoinRequestMutation } from '@/core/services/network'
import { LIST_ENDPOINT_MAX_LIMIT } from '@/core/utils/list-response.util'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { isExpertiseOrder } from '@/utils/type-guards'

import { ExpertiseValidationStepperContext } from '../ExpertiseValidationPage'

export default function SelectNetworkStep() {
  const { handleChange: handleStepperValuesChange, values } = useContext(ExpertiseValidationStepperContext)
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: rawNetworksResponse, refetch: refetchNetworks } = useGetNetworksQuery(
    { limit: LIST_ENDPOINT_MAX_LIMIT },
    { refetchOnMountOrArgChange: true }
  )
  const rawNetworks = rawNetworksResponse?.items
  const [requestJoinNetwork, { isLoading: isRequestingJoin }] = useSendNetworkJoinRequestMutation()

  const [requiredJoinNetworkId, setRequiredJoinNetworkId] = useState<string>()

  const networks = useMemo(
    () =>
      _.map(rawNetworks, (network) => ({
        id: network._id,
        coverPhoto: network.coverPhoto?.url,
        joined: !!network.members.find((member) => member === authUser?._id),
        memberCount: network.members.length,
        name: network.name,
        pendingOrders: [
          {
            count: _.filter(network.pendingOrders, isExpertiseOrder).length,
            type: 'expertise' as const,
          },
        ],
        privacy: network.privacy,
      })),
    [rawNetworks, authUser]
  )

  return (
    <div>
      <Grid container spacing={2}>
        {networks.map((network) => {
          const isJoinRequestPending =
            network.privacy === 'private' &&
            !!_.find(rawNetworks, ['_id', network.id])?.pendingMembers.find((pendingMember) => pendingMember._id === authUser?._id)
          return (
            <Grid key={network.id} item xs={12} md={6}>
              <NetworkCard
                isJoinRequestPending={isJoinRequestPending}
                network={network}
                onSelect={(networkId) => {
                  const network = rawNetworks?.find((network) => network._id === networkId)

                  if (!network) return

                  if (network.privacy === 'public' || !!_.find(network?.members, (member) => member === authUser?._id)) {
                    handleStepperValuesChange('network', values.network !== networkId ? networkId : undefined)
                  } else {
                    setRequiredJoinNetworkId(network._id)
                  }
                }}
                selected={network.id === values.network}
              />
            </Grid>
          )
        })}
      </Grid>
      <JoinNetworkRequirementDialog
        isRequesting={isRequestingJoin}
        open={!!requiredJoinNetworkId}
        onClose={() => {
          setRequiredJoinNetworkId(undefined)
        }}
        onJoin={async () => {
          if (!requiredJoinNetworkId) return
          try {
            await requestJoinNetwork(requiredJoinNetworkId).unwrap()
            refetchNetworks()
            setRequiredJoinNetworkId(undefined)
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.network.requestJoin.error')), { variant: 'error' })
          }
        }}
      />
    </div>
  )
}
